import { Form, FormElementRef } from '@kirz/mui-admin';
import {
  Button,
  Typography,
  Card,
  Box,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Alert,
} from '@mui/material';
import React, { useContext, useEffect, useRef } from 'react';

import { UserContext } from 'contexts/UserContext';
import { WebFormContext } from 'contexts/WebFormContext';
import { api } from 'services/api';

export type StepWrapperProps = {
  name: string;
  description?: string | null;
  maxWidth?: number;
  children: React.ReactNode;
  backwardStep?: number | ((data: any) => number);
  forwardStep?: number | ((data: any) => number);
};

export function StepWrapper({
  name,
  description,
  maxWidth = 700,
  children,
  backwardStep = 1,
  forwardStep = 1,
}: StepWrapperProps) {
  const theme = useTheme();
  const { user } = useContext(UserContext);
  const formRef = useRef<FormElementRef>(null);
  const { form, setForm, totalSteps, step, setStep } =
    useContext(WebFormContext);
  const formData = useRef(form.data);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    formData.current = form.data;
  }, [form.data]);

  return (
    <Box
      sx={{
        width: {
          xs: '100%',
          md: maxWidth,
        },
        mx: 'auto',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Form
        ref={formRef}
        shouldUnregister={false}
        dirtySubmit={false}
        readOnly={form.isFilled}
        onSubmit={async (data) => {
          if (form.isFilled) {
            return;
          }

          const newStep =
            step +
            (typeof forwardStep === 'function'
              ? forwardStep(data)
              : forwardStep);
          setStep(newStep);
          setForm((prev) => ({
            ...prev,
            data: { ...prev.data, ...data, step: newStep },
          }));

          if (newStep === totalSteps - 1) {
            setForm((prev) => ({
              ...prev,
              isFilled: true,
              filledAt: new Date().toISOString(),
            }));

            await api.fillForm(form!.id);
          }
        }}
        defaultValues={{
          voornaam: (user as any)?.firstName,
          naam: (user as any)?.lastName,
          email: (user as any)?.email,
          rijksregister: (user as any)?.nationalRegister,
          adres: (user as any)?.address,
          ...(form.data ?? {}),
        }}
        sx={{
          width: '100%',
        }}
      >
        <Card
          elevation={isMobile ? 0 : 10}
          sx={{
            width: '100%',
            minHeight: 500,
            maxHeight: 'calc(100vh - 64px)', // Leave space for top margin
            borderRadius: isMobile ? 0 : 4,
            px: { xs: 2, sm: 6 },
            py: { xs: 0, sm: 2 },
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {form.isFilled && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Dit formulier is ingevuld op{' '}
              {new Date(form.filledAt!).toLocaleDateString()}
            </Alert>
          )}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 4 }}>
              <Box
                component="img"
                src="/icons/logo_2015_MP_only_twitter.png"
                sx={{
                  width: 80,
                  height: 80,
                }}
              />
              <Box
                sx={{
                  display: 'flex',
                  flex: 1,
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: 1,
                  mt: 2,
                }}
              >
                <LinearProgress
                  variant="determinate"
                  value={(step / (totalSteps - 1)) * 100}
                  sx={{
                    width: '100%',
                    height: 5,
                    borderRadius: 5,
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ alignSelf: 'center' }}
                    color="text.secondary"
                  >
                    {form.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      alignSelf: 'center',
                      display: { xs: 'none', sm: 'block' },
                    }}
                    color="text.secondary"
                  >
                    Stap {step} van {totalSteps - 1}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      alignSelf: 'center',
                      display: { xs: 'block', sm: 'none' },
                    }}
                    color="text.secondary"
                  >
                    {step}/{totalSteps}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              {name}
            </Typography>
            {description && (
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                {description}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowX: 'hidden',
              overflowY: 'auto',
              pr: 1,
              pt: 1,
              pb: 3,
            }}
          >
            {children}
          </Box>

          <Box
            sx={{
              display: 'flex',
              borderTop: `1px solid ${theme.palette.divider}`,
              pt: 2,
              mt: 'auto',
            }}
          >
            {step > 1 && (
              <Button
                variant="outlined"
                color="primary"
                sx={{ minWidth: 120 }}
                onClick={() => {
                  setStep(
                    step -
                      (typeof backwardStep === 'function'
                        ? backwardStep(formData.current)
                        : backwardStep),
                  );
                }}
              >
                Vorige
              </Button>
            )}
            {step < totalSteps - 1 && (
              <Button
                variant="contained"
                color="primary"
                sx={{ minWidth: 120, ml: 'auto' }}
                onClick={() => {
                  if (form.isFilled) {
                    setStep(step + 1);
                    return;
                  }

                  formRef.current?.submit();
                }}
              >
                Volgende
              </Button>
            )}
          </Box>
        </Card>
      </Form>
    </Box>
  );
}
